import { inject, Injectable, signal } from '@angular/core';
import { ApiService } from '../api/api.service';
import { API_ROUTE_ZAEHLPUNKTE, getApiUrl, ROUTE_NOT_FOUND } from '../../app.constants';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { Address } from '@mwe/models';
import { MeterPoint } from '../../model/meter-point.model';
import { IAdresse, transformAdresseToAddress } from '@shared/model/address.model';
import {
  IFindZaehlpunktFilter,
  IPreisbestandteil,
  IZaehlpunkt,
  MeterPointExportRequest,
  transformResponseToMeterPoint,
} from '../../model/meter-point-http.model';
import { Router } from '@angular/router';
import { homeRoutePrefix } from '@shared/app.constants';
import { MeterPointOverviewRequest } from '../../model/meterPointOverviewRequest.model';
import { MeterPointOverviewResponse } from '../../model/meterPointOverviewResponse.model';
import { IkpPopupService } from '@shared/service/popup/popup.service';
import { MeterPointIdentifierSearchResult } from '../../model/meterpoint-identifier-search.model';
import { HttpResponse } from '@angular/common/http';
import dayjs from 'dayjs';

@Injectable({
  providedIn: 'root',
})
export class MeterPointService extends ApiService {
  private readonly popupService = inject(IkpPopupService);
  private readonly router = inject(Router);
  meterPoints = signal<MeterPoint[]>([]);

  loadMeterPoint(contractNumber: string, meterpointId: string): Observable<MeterPoint> {
    const apiPath = getApiUrl(this.apiUrl, API_ROUTE_ZAEHLPUNKTE) + `/${contractNumber}/${meterpointId}`;
    return this.httpClient.get<IZaehlpunkt>(apiPath).pipe(
      map((findZaehlpunktResponse: IZaehlpunkt) => {
        const meterPoint: MeterPoint = transformResponseToMeterPoint(findZaehlpunktResponse);
        this.meterPoints.set([meterPoint]);
        return meterPoint;
      }),
      catchError(error => {
        if (error.status === 400) {
          this.router.navigate([homeRoutePrefix(ROUTE_NOT_FOUND)]);
          return;
        }
        console.error('Error fetching zaehlpunkt:', error);
        this.handleErrorMeterpoint();
        return throwError(() => error);
      }),
    );
  }

  loadMeterPointCount(requestBody?: IFindZaehlpunktFilter): Observable<number> {
    return this.httpClient.post<number>(getApiUrl(this.apiUrl, `${API_ROUTE_ZAEHLPUNKTE}/count`), requestBody).pipe(
      catchError(error => {
        console.error('Error fetching meter-points count:', error);
        this.handleErrorMeterpoints();
        return [];
      }),
    );
  }

  loadAddresses(gpNr?: string): Observable<Address[]> {
    let url = getApiUrl(this.apiUrl, `${API_ROUTE_ZAEHLPUNKTE}/adressen`);
    if (gpNr) {
      url += `?gpNr=${encodeURIComponent(gpNr)}`;
    }
    return this.httpClient.get<IAdresse[]>(url).pipe(
      map((addressData: IAdresse[]) => addressData.map(transformAdresseToAddress)),
      catchError(error => {
        console.error('Error fetching addresses:', error);
        this.handleErrorMeterpoints();
        return of([]);
      }),
    );
  }

  loadPriceInformation(contractNumber: string, meterpointId: string): Observable<IPreisbestandteil[]> {
    return this.httpClient
      .get<IPreisbestandteil[]>(getApiUrl(this.apiUrl, `${API_ROUTE_ZAEHLPUNKTE}/${contractNumber}/${meterpointId}/preise`))
      .pipe(
        catchError(error => {
          console.error('Error fetching price information:', error);
          this.handleErrorMeterpoint();
          return [];
        }),
      );
  }

  loadMeterPointOverview(requestBody: MeterPointOverviewRequest): Observable<MeterPointOverviewResponse> {
    return this.httpClient.post<MeterPointOverviewResponse>(getApiUrl(this.apiUrl, API_ROUTE_ZAEHLPUNKTE), requestBody).pipe(
      catchError(error => {
        console.error('Error fetching zaehlpunkt-overview:', error);
        this.handleErrorMeterpoints();
        return [];
      }),
    );
  }

  exportZaehlpunkteCSV(requestBody: MeterPointExportRequest): Observable<HttpResponse<Blob>> {
    return this.httpClient
      .post(getApiUrl(this.apiUrl, `${API_ROUTE_ZAEHLPUNKTE}/csv`), requestBody, {
        headers: { Accept: 'text/csv; charset=utf-8' },
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(map((response: HttpResponse<Blob>) => response.clone({ body: this.addBlobUtf8Encoding(response.body) })));
  }

  createMeterpointExportFilename(): string {
    const now = dayjs().format('DD.MM.YYYY');
    return `Zaehlpunkte_Export_${now}.csv`;
  }

  searchZaehlpunktIdentifier(searchTerm: string, limit = 100): Observable<MeterPointIdentifierSearchResult> {
    if (searchTerm.replaceAll('\\s', '').length < 3) {
      return of({ zaehlpunktNummern: [], zaehlerNummern: [] });
    }
    return this.httpClient.get<MeterPointIdentifierSearchResult>(getApiUrl(this.apiUrl, `${API_ROUTE_ZAEHLPUNKTE}/search/identifiers`), {
      params: {
        searchTerm: searchTerm,
        limit: limit,
      },
    });
  }

  private handleErrorMeterpoint(): void {
    this.popupService.handleErrorPopup('download-error', 'global.errors.meterpoint.title');
  }

  private handleErrorMeterpoints(): void {
    this.popupService.handleErrorPopup('download-error', 'global.errors.meterpoints.title');
  }
}
